'use client';

import { useRouter } from 'nextjs-toploader/app';
import { useEffect, useMemo, useRef } from 'react';

import type { Id } from '@/convex/_generated/dataModel';
import { api, useQueryWithStatus } from '@/lib/convex';
import {
  isSafeRelativeDeepLink,
  toDesktopNotificationItem,
} from '@/lib/desktop-notification-contract';
import { currentDesktopNotificationBridge } from '@/lib/desktop-notifications';
import {
  diffDesktopNotifications,
  type DesktopNotificationDiffState,
} from '@/lib/notifications/desktop-notification-diff';
import { desktopNotificationBadge } from '@/lib/notifications/desktop-notification-status';
import { hasBrowserNotificationOptOut } from '@/lib/notifications/web-push-client';
import type { BrowserNotificationPermissionController } from './browser-notification-permission';

const WATCH_PAGE_SIZE = 20;

/**
 * Inside the desktop app, forwards new unread platform notifications for the
 * current workspace to native main and mirrors the unread badge. The website
 * and older desktop builds skip every query here.
 */
export function useDesktopNativeNotifications(
  organizationId: Id<'organizations'> | undefined,
  permission: BrowserNotificationPermissionController
) {
  const router = useRouter();
  const bridge = useMemo(
    () =>
      permission.surface === 'desktop'
        ? currentDesktopNotificationBridge()
        : null,
    [permission.surface]
  );
  const active =
    bridge !== null &&
    organizationId !== undefined &&
    permission.status === 'enabled';
  const inbox = useQueryWithStatus(
    api.notifications.public.listNotifications,
    active && organizationId
      ? {
          organizationId,
          paginationOpts: { numItems: WATCH_PAGE_SIZE, cursor: null },
        }
      : 'skip'
  );
  const summary = useQueryWithStatus(
    api.notifications.public.getNotificationSummary,
    active && organizationId ? { organizationId } : 'skip'
  );
  const diff = useRef<DesktopNotificationDiffState | null>(null);

  useEffect(() => {
    // Turning alerts off or switching workspace restarts from a new backlog.
    if (!active) diff.current = null;
  }, [active, organizationId]);

  const page = inbox.data?.page;
  useEffect(() => {
    if (!bridge || !active || !organizationId || !page) return;
    const next = diffDesktopNotifications(diff.current, organizationId, page);
    diff.current = next.state;
    if (hasBrowserNotificationOptOut()) return;
    for (const candidate of next.fresh) {
      const item = toDesktopNotificationItem(candidate);
      if (item) void bridge.show(item).catch(() => undefined);
    }
  }, [active, bridge, organizationId, page]);

  const badge = desktopNotificationBadge(active, summary.data?.unreadCount);
  useEffect(() => {
    if (!bridge) return;
    void bridge.setBadge(badge).catch(() => false);
  }, [badge, bridge]);
  useEffect(() => {
    if (!bridge) return;
    return () => {
      void bridge.setBadge(0).catch(() => false);
    };
  }, [bridge]);

  useEffect(() => {
    if (!bridge) return;
    return bridge.subscribeOpen((deepLink) => {
      if (isSafeRelativeDeepLink(deepLink)) router.push(deepLink);
    });
  }, [bridge, router]);
}
